const LOCALE = "pt-BR";

// The API's currency is a symbol ("R$", "US$", "€"), not an ISO code, so
// format the number as pt-BR decimal and prefix the symbol.
export const formatMoney = (value, currency) => {
    const number = Number(value);
    if (value === null || value === undefined || Number.isNaN(number)) return "";
    const formatted = number.toLocaleString(LOCALE, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
    return `${currency ?? ""} ${formatted}`.trim();
};

export const formatDate = (value) => (value ? new Date(value).toLocaleDateString(LOCALE) : "");

/** "04/10/2026 14:32" */
export const formatDateTime = (value) =>
    value ? new Date(value).toLocaleString(LOCALE, {dateStyle: "short", timeStyle: "short"}) : "";

const RELATIVE = new Intl.RelativeTimeFormat(LOCALE, {numeric: "auto"});
const UNITS = /** @type {[Intl.RelativeTimeFormatUnit, number][]} */ ([
    ["minute", 60],
    ["hour", 3600],
    ["day", 86400],
]);

/**
 * How long ago, in words: "agora há pouco", "há 5 minutos", "há 2 horas", "ontem",
 * "há 3 dias"; a plain date once it is a month old or more.
 * @param {string|Date|null|undefined} value
 * @param {number} [now] milliseconds, for tests
 */
export const formatAgo = (value, now = Date.now()) => {
    if (!value) return "";
    const seconds = Math.max(0, Math.round((now - new Date(value).getTime()) / 1000));
    if (seconds < 60) return "agora há pouco";
    if (seconds >= 30 * 86400) return formatDate(value);
    const [unit, size] = [...UNITS].reverse().find(([, size]) => seconds >= size) ?? UNITS[0];
    return RELATIVE.format(-Math.floor(seconds / size), unit);
};

// Device type fields come as a comma-separated string (or null).
export const parseFieldsList = (value) =>
    (value ?? "")
        .split(",")
        .map((f) => f.trim())
        .filter(Boolean);

/**
 * WhatsApp link for a phone number written in international format
 * ("+" and the country code first, e.g. "+55 (11) 99999-9999"), or null when
 * the number isn't in that format and can't be linked reliably.
 * @param {string|null|undefined} phone
 */
export const whatsappUrl = (phone) => {
    const compact = (phone ?? "").trim().replace(/[\s().-]/g, "");
    // E.164: "+", a country code that doesn't start with 0, 7 to 15 digits in all.
    return /^\+[1-9]\d{6,14}$/.test(compact) ? `https://wa.me/${compact.slice(1)}` : null;
};

/**
 * `mailto:` link for an email address, or null when the text doesn't look
 * like one (so it is shown as plain text instead of a broken link).
 * @param {string|null|undefined} email
 */
export const mailtoUrl = (email) => {
    const address = (email ?? "").trim();
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address) ? `mailto:${address}` : null;
};

/**
 * Google Maps search link for a street address, or null when there is nothing
 * to look up. `area` (e.g. the city) narrows the search when the address
 * doesn't say where it is.
 * @param {string|null|undefined} address
 * @param {string|null|undefined} [area]
 */
export const mapsUrl = (address, area) => {
    const street = (address ?? "").trim();
    // no street, nothing to look up (a city alone is not an address)
    const query = street ? [street, (area ?? "").trim()].filter(Boolean).join(", ") : "";
    return query ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}` : null;
};

/**
 * "09:00:00" (how the API sends a time of day) -> "09:00".
 * @param {string|null|undefined} time
 */
export const formatTime = (time) => (time ?? "").slice(0, 5);

/**
 * 7 -> "7 dias", 1 -> "1 dia".
 * @param {number|string|null|undefined} days
 */
export const formatDays = (days) => {
    if (days === null || days === undefined || days === "") return "";
    return `${days} ${Number(days) === 1 ? "dia" : "dias"}`;
};

/**
 * 29.8 -> "29,8%": one decimal at most, pt-BR style.
 * @param {number|null|undefined} percent
 */
export const formatPercent = (percent) => {
    if (percent === null || percent === undefined || Number.isNaN(percent)) return "";
    return `${percent.toLocaleString(LOCALE, {maximumFractionDigits: 1})}%`;
};
